import { CircularProgress, Stack } from '@mui/material';
import { MutateOptions } from '@tanstack/react-query';
import { ComponentType, createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { TaskLike, TaskStatus, WaitingOnReason } from './MemberOperator.interfaces';
import useMemberOperatorDatadog from './hooks/useMemberOperatorDatadog';
import useMemberOperatorSegment from './hooks/useMemberOperatorSegment';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import useOperatorTask from 'services/operatorTasks/queries/useOperatorTask';
import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';

interface MemberOperatorContextValue {
    task: TaskLike | undefined;
    isPending: boolean;
    refetch: () => void;
    updateTaskStatus: (
        params: {
            status: TaskStatus;
            execute_handlers_async?: boolean;
            completion_payload?: Record<string, any>;
            waiting_on?: WaitingOnReason;
        },
        mutateOptions?: MutateOptions<number, Error, unknown>
    ) => void;
    sendTrackingEvent: (eventName: string, properties?: Record<string, unknown>) => void;
}

const MemberOperatorContext = createContext<MemberOperatorContextValue | null>(null);

export function useMemberOperatorTaskContext() {
    const context = useContext(MemberOperatorContext);
    if (!context) {
        throw new Error('useMemberOperatorTaskContext must be used within a MemberOperatorProvider');
    }
    return context;
}

const DEFAULT_RENDER_STATUSES: TaskStatus[] = [TaskStatus.READY, TaskStatus.IN_PROGRESS];

type MemberOperatorProviderProps = ({ taskId: string; task?: never } | { task: TaskLike; taskId?: never }) & {
    children?: ReactNode;
    renderStatus?: string[] | Partial<Record<string, ComponentType>>;
    loader?: ComponentType;
    // Auto-fire "Task Viewed" Segment/Datadog on mount
    trackViewOnMount?: boolean;
    // Extra properties merged into every sendTrackingEvent call from this provider (e.g. group
    // context for a task opened inside a group drawer). Per-call properties still take precedence.
    trackingProperties?: Record<string, unknown>;
};

function getRenderedContent(
    task: TaskLike | undefined,
    renderStatus: string[] | Partial<Record<string, ComponentType>>,
    children: ReactNode
): ReactNode | null {
    if (!task) {
        return null;
    }

    if (Array.isArray(renderStatus)) {
        return renderStatus.includes(task.status) ? children : null;
    }

    // Record<TaskStatus, ComponentType> — lazily render the component for the matching status
    const StatusComponent = renderStatus[task.status];
    if (StatusComponent) {
        return <StatusComponent />;
    }

    return null;
}

export default function MemberOperatorProvider({
    children,
    renderStatus = DEFAULT_RENDER_STATUSES,
    loader: Loader,
    trackViewOnMount = true,
    trackingProperties,
    ...props
}: MemberOperatorProviderProps) {
    const taskId = 'taskId' in props ? props.taskId : undefined;
    const providedTask = 'task' in props ? props.task : undefined;

    const { data: fetchedTask, isPending, refetch } = useOperatorTask(taskId);

    const task = providedTask ?? fetchedTask;

    const { mutate } = useUpdateOperatorTaskStatus();
    const { sendTrackingEvent: sendEvent } = useEventTracking();

    useMemberOperatorSegment(task, trackViewOnMount);
    useMemberOperatorDatadog(task, trackViewOnMount);

    const updateTaskStatus = useCallback(
        (
            params: {
                status: TaskStatus;
                execute_handlers_async?: boolean;
                completion_payload?: Record<string, any>;
                waiting_on?: WaitingOnReason;
            },
            mutateOptions?: MutateOptions<number, Error, unknown>
        ) => {
            if (!task) {
                return;
            }
            mutate({ id: task.id, ...params }, mutateOptions as Parameters<typeof mutate>[1]);
        },
        [task, mutate]
    );

    const sendTrackingEvent = useCallback(
        (eventName: string, properties?: Record<string, unknown>) => {
            sendEvent(eventName, {
                task_id: task?.id,
                task_type: task?.task_type,
                task_status: task?.status,
                task_template_name: task?.task_template.name,
                ...trackingProperties,
                ...properties,
            });
        },
        [task, sendEvent, trackingProperties]
    );

    const contextValue = useMemo<MemberOperatorContextValue>(
        () => ({
            task,
            isPending: taskId ? isPending : false,
            refetch,
            updateTaskStatus,
            sendTrackingEvent,
        }),
        [task, isPending, taskId, refetch, updateTaskStatus, sendTrackingEvent]
    );

    // Show loader while fetching by taskId
    if (taskId && isPending) {
        if (Loader) {
            return <Loader />;
        }
        return (
            <Stack justifyContent="center" alignItems="center" marginTop={4}>
                <CircularProgress color="primary" />
            </Stack>
        );
    }

    const content = getRenderedContent(task, renderStatus, children);
    if (content === null) {
        return null;
    }

    return <MemberOperatorContext.Provider value={contextValue}>{content}</MemberOperatorContext.Provider>;
}
